import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import {
  VpnKey,
  Delete,
  Refresh,
  Info,
  Warning
} from "@material-ui/icons";
import { toast } from "react-toastify";
import useTokenApiMaster from "../../hooks/useTokenApiMaster";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: 800,
    margin: "0 auto"
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  title: {
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  tokenField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  infoBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === "light" ? "#fff9c4" : "#f57f17",
    borderRadius: theme.shape.borderRadius
  },
  alertBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1)
  },
  alertInfo: {
    backgroundColor: theme.palette.mode === "light" ? "#e3f2fd" : "#1565c0",
    color: theme.palette.mode === "light" ? "#1976d2" : "#ffffff"
  },
  alertWarning: {
    backgroundColor: theme.palette.mode === "light" ? "#fff3e0" : "#e65100",
    color: theme.palette.mode === "light" ? "#f57c00" : "#ffffff"
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3)
  }
}));

const TokenApiMaster = () => {
  const classes = useStyles();
  const { get, generate, revoke } = useTokenApiMaster();
  
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [showToken, setShowToken] = useState(true); // Sempre mostrar o token
  const [hasToken, setHasToken] = useState(false);
  const [tokenPreview, setTokenPreview] = useState(null);

  useEffect(() => {
    loadTokenStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTokenStatus = async () => {
    try {
      setLoading(true);
      const data = await get();
      setHasToken(data.hasToken);
      setTokenPreview(data.tokenPreview);
      // Se temos o token completo, sempre mostrar
      if (data.token) {
        setToken(data.token);
      } else {
        setToken(null);
      }
    } catch (err) {
      toast.error("Erro ao carregar status do token");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const data = await generate();
      setToken(data.token);
      setHasToken(true);
      setTokenPreview(`${data.token.substring(0, 8)}...`);
      toast.success("Token gerado com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erro ao gerar token");
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("Tem certeza que deseja revogar o token? Esta ação não pode ser desfeita.")) {
      return;
    }

    try {
      setLoading(true);
      await revoke();
      setToken(null);
      setHasToken(false);
      setTokenPreview(null);
      toast.success("Token revogado com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erro ao revogar token");
    } finally {
      setLoading(false);
    }
  };


  if (loading && !hasToken && !token) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Paper className={classes.paper} elevation={2}>
        <Typography variant="h6" className={classes.title}>
          <VpnKey /> TOKEN API MASTER
        </Typography>

        <Paper className={`${classes.alertBox} ${classes.alertInfo}`} elevation={0}>
          <Info style={{ marginTop: 2 }} />
          <Typography variant="body2">
            O TOKEN_API_MASTER permite acesso total a todas as rotas e APIs do sistema.
            Este token não expira e deve ser mantido em segurança.
          </Typography>
        </Paper>

        {hasToken ? (
          <>
            {token ? (
              <TextField
                fullWidth
                label="Token API Master"
                value={token}
                className={classes.tokenField}
                InputProps={{
                  readOnly: true
                }}
              />
            ) : (
              <Paper className={`${classes.alertBox} ${classes.alertWarning}`} elevation={0}>
                <Warning style={{ marginTop: 2 }} />
                <Typography variant="body2">
                  Token existe mas não está disponível para visualização. 
                  Gere um novo token para visualizá-lo.
                </Typography>
              </Paper>
            )}

            <Box className={classes.buttonGroup}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Refresh />}
                onClick={handleGenerate}
                disabled={loading}
              >
                Gerar Novo Token
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<Delete />}
                onClick={handleRevoke}
                disabled={loading}
              >
                Revogar Token
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Nenhum token foi gerado ainda. Gere um token para começar a usar a API.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<VpnKey />}
              onClick={handleGenerate}
              disabled={loading}
            >
              Gerar Token
            </Button>
          </>
        )}

        <Box className={classes.infoBox}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Como usar:</strong>
          </Typography>
          <Typography variant="body2" component="div">
            <code style={{ display: "block", marginTop: 8 }}>
              Authorization: Bearer {"{"}seu_token{"}"}
            </code>
          </Typography>
          <Typography variant="body2" style={{ marginTop: 8, fontSize: "0.875rem" }}>
            Use este token no header Authorization de todas as requisições à API.
            O token fornece acesso completo como administrador da empresa.
          </Typography>
        </Box>
      </Paper>
    </div>
  );
};

export default TokenApiMaster;
